import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { useDebounce } from 'src/components/helpers/useDebounce';
import { useTheme } from 'styled-components';
import {
  SearchIconWrapper,
  SearchInputFieldRow,
  SearchInputInput,
  SearchInputLabel,
  SearchInputWrapper,
} from './SearchInput.styles';

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_PLACEHOLDER = 'Search by all fields';
const DEFAULT_LABEL = 'Search';

export interface SearchInputProps {
  onSearch?: (term: string) => void;
  debounceMs?: number;
  placeholder?: string;
  label?: string;
  hideLabel?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  id?: string;
  'data-testid'?: string;
  disabled?: boolean;
  'aria-label'?: string;
  $width?: string;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  onSearch,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  placeholder = DEFAULT_PLACEHOLDER,
  label = DEFAULT_LABEL,
  hideLabel = false,
  value: controlledValue,
  defaultValue = '',
  onChange,
  id,
  'data-testid': dataTestId,
  disabled = false,
  'aria-label': ariaLabel,
  $width,
}) => {
  const theme = useTheme();
  const [internalValue, setInternalValue] = useState(defaultValue);
  const valueRef = useRef(controlledValue ?? defaultValue);
  const isControlled = controlledValue !== undefined;

  const currentValue = isControlled ? controlledValue : internalValue;

  useEffect(() => {
    valueRef.current = currentValue ?? '';
  }, [currentValue]);

  const triggerSearch = useCallback(() => {
    onSearch?.(valueRef.current ?? '');
  }, [onSearch]);

  const debouncedSearch = useDebounce(triggerSearch, debounceMs);

  useEffect(() => {
    return () => {
      if (typeof debouncedSearch.cancel === 'function') {
        debouncedSearch.cancel();
      }
    };
  }, [debouncedSearch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    valueRef.current = newValue;
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(e);
    debouncedSearch();
  };

  const inputId = id ?? dataTestId ?? 'search-input';
  const labelHtmlFor = inputId;

  return (
    <SearchInputWrapper $width={$width}>
      {!hideLabel && (
        <SearchInputLabel htmlFor={labelHtmlFor}>{label}</SearchInputLabel>
      )}
      <SearchInputFieldRow $noLabelMargin={hideLabel}>
        <SearchInputInput
          id={inputId}
          type="text"
          role="searchbox"
          placeholder={placeholder}
          value={currentValue ?? ''}
          onChange={handleChange}
          disabled={disabled}
          data-testid={dataTestId}
          aria-label={ariaLabel ?? label}
        />
        <SearchIconWrapper>
          <StyledIcon
            className="uil uil-search"
            size="20px"
            color={theme.atlas.semantic.color.type.default}
            aria-hidden
          />
        </SearchIconWrapper>
      </SearchInputFieldRow>
    </SearchInputWrapper>
  );
};
